import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { History, EmojiEvents } from '@material-ui/icons';
import FortIcon from 'components/Icons/FortIcon';
import { useHistory, useLocation } from 'react-router-dom';
import clsx from 'clsx';

// The public site only shows results; groups and the boss explorer live in the app.
const PAGES = [
  {path: '/pve/runs', label: 'Past Runs', subtitle: 'Every recorded run, with pulls, damage and healing.', Icon: History},
  {path: '/pve/leaderboard', label: 'Leaderboards', subtitle: 'Fastest clears and boss kills.', Icon: EmojiEvents},
];

const ACCENT = '#42a5f5';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(28, 29, 35, 0.97), rgba(18, 18, 22, 0.97))',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.3)',
  },
  top: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2.25, 2.75, 2),
    background: 'radial-gradient(ellipse at top left, rgba(66, 165, 245, 0.14), transparent 55%)',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'auto minmax(0, 1fr)',
    },
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: 13,
    display: 'grid',
    placeItems: 'center',
    color: ACCENT,
    background: 'rgba(66, 165, 245, 0.14)',
    border: '1px solid rgba(66, 165, 245, 0.32)',
    '& svg': {
      fontSize: 26,
    },
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    marginTop: 3,
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  actions: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
      justifyContent: 'start',
    },
  },
  tabs: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: 4,
    padding: theme.spacing(0, 2),
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(0, 0, 0, 0.15)',
  },
  tab: {
    position: 'relative',
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    alignItems: 'center',
    height: 46,
    padding: theme.spacing(0, 1.75),
    fontSize: '0.92rem',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'color 0.15s',
    '& svg': {
      fontSize: 18,
      opacity: 0.8,
    },
    '&:hover': {
      color: '#fff',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 10,
      right: 10,
      bottom: 0,
      height: 3,
      borderRadius: '3px 3px 0 0',
      background: 'transparent',
      transition: 'background 0.15s',
    },
  },
  tabActive: {
    color: '#fff',
    '& svg': {
      color: ACCENT,
      opacity: 1,
    },
    '&::after': {
      background: ACCENT,
      boxShadow: `0 0 10px ${ACCENT}`,
    },
  },
}));

interface Props {
  /** shown on the right of the header, e.g. search */
  actions?: React.ReactNode;
}

/** The PvE pages' header: title, the current page's description, and tabs across the pages. */
export const PveNav: React.FC<Props> = ({actions}) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const page = PAGES.slice().reverse().find((p) => location.pathname == p.path || location.pathname.startsWith(p.path + '/')) || PAGES[0];
  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <div className={classes.icon}><FortIcon /></div>
        <div style={{minWidth: 0}}>
          <div className={classes.title}>Dungeons &amp; Raids</div>
          <div className={classes.subtitle}>{page.subtitle}</div>
        </div>
        {actions && <div className={classes.actions}>{actions}</div>}
      </div>
      <div className={classes.tabs} role="tablist">
        {PAGES.map((p) => (
          <div key={p.path} role="tab" aria-selected={p.path == page.path} className={clsx(classes.tab, {[classes.tabActive]: p.path == page.path})}
            onClick={() => p.path != page.path && history.push(p.path)}>
            <p.Icon />
            <span>{p.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
